// Copyright (c) Docugami, Inc. All rights reserved.

import { chunkToolbarColor } from "components/shell/presentation/AppStyles";
import { useChunkSelection } from "hooks/chunkSelection";
import { dgIconNames } from "hooks/dgIcons";
import { useDocumentDgml, AxisIds, ChunkAxis, DgmlInstancesSingleton } from "hooks/documentDgml";
import { useSdvResource } from "hooks/SdvResource";
import { useMemo, useEffect, useState } from "react";
import { Engine } from "sdv/engine/Engine";
import { Mode } from "sdv/Mode";
import { ToolbarButton } from "./ChunkToolbar";
import { ToolbarItemProps, ToolType } from "./ToolbarItem";
import { selectSelectedChunkInstance } from "store/sdvSelectors";
import { useAppSelector } from "hooks/redux";

interface INavigationTools {
    tools: ToolbarItemProps[][];
}

export function useNavigationTools(documentId: string, engine: Engine | null): INavigationTools {
    const selectedChunkInstance = useAppSelector(selectSelectedChunkInstance);
    const { selectChunk } = useChunkSelection();

    const [hoveredButton, setHoveredButton] = useState<ToolbarButton | null>(null);

    const dgml = useDocumentDgml();
    const canvasMode = useSdvResource("mode", engine).value;

    const navigationAxisIds = useMemo((): AxisIds => {
        if (!!selectedChunkInstance && dgml.isInitialized()) {
            return dgml.getAxisIds(selectedChunkInstance.spanId!);
        }

        return {};
    }, [selectedChunkInstance, dgml]);

    const { set: setHoverSpanId } = useSdvResource('chunkToolbarHoverSpanId', engine);

    useEffect(() => {
        if (!engine) {
            return;
        }

        let targetSpanId: string | undefined;
        if (!!hoveredButton?.axisId) {
            const instanceId = navigationAxisIds[hoveredButton.axisId];
            const instance = DgmlInstancesSingleton.getInstanceById(documentId, instanceId ?? '');
            targetSpanId = instance?.spanId;
        }

        setHoverSpanId(targetSpanId);
    }, [navigationAxisIds, setHoverSpanId, engine, documentId, hoveredButton])

    const navigationTools = useMemo(() => {
        function onClick(toolButton: ToolbarButton) {
            if (!!toolButton.axisId && !!navigationAxisIds[toolButton.axisId]) {
                selectChunk(navigationAxisIds[toolButton.axisId]!);
            }
        }

        const tools: ToolbarItemProps[][] = [];
        toolButtonProperties.forEach(x => {
            const list: ToolbarItemProps[] = [];
            x.forEach(y => {
                list.push({
                    id: y.type,
                    iconProps: y.iconProps,
                    isSelected: false,
                    onClick: () => onClick(y),
                    onHover: (isHovered: boolean) => setHoveredButton(isHovered ? y : null),
                    disabled: !y.axisId || !navigationAxisIds[y.axisId] || canvasMode !== Mode.Selection
                });
            });
            tools.push(list);
        });

        return tools;
    }, [navigationAxisIds, selectChunk, canvasMode]);

    return {
        tools: navigationTools
    };
}

const toolButtonProperties: (ToolbarButton[])[] = [
    [
        {
            type: ToolType.Parent,
            iconProps: {
                source: {
                    name: dgIconNames.DgChunkToolbarParentIcon,
                    height: 1,
                    width: 1,
                    defaultColor: chunkToolbarColor
                },
                width: 20,
                height: 20
            },
            axisId: ChunkAxis?.Parent
        },
        {
            type: ToolType.Child,
            iconProps: {
                source: {
                    name: dgIconNames.DgChunkToolbarChildIcon,
                    height: 1,
                    width: 1,
                    defaultColor: chunkToolbarColor
                },
                width: 20,
                height: 20
            },
            axisId: ChunkAxis?.FirstChild
        }
    ],
    [
        {
            type: ToolType.Previous,
            iconProps: {
                source: {
                    name: dgIconNames.DgChunkToolbarPrevSiblingIcon,
                    height: 1,
                    width: .875,
                    defaultColor: chunkToolbarColor
                },
                width: 14,
                height: 16,
                containerHeight: 20,
                containerWidth: 20
            },
            axisId: ChunkAxis?.PreviousSibling
        },
        {
            type: ToolType.Next,
            iconProps: {
                source: {
                    name: dgIconNames.DgChunkToolbarNextSiblingIcon,
                    height: 1,
                    width: .875,
                    defaultColor: chunkToolbarColor
                },
                width: 14,
                height: 16,
                containerHeight: 20,
                containerWidth: 20
            },
            axisId: ChunkAxis?.NextSibling
        }
    ]
];
